/**
 * Conta de cor para o tema personalizado: ler hex, medir contraste
 * (WCAG 2.x) e escolher a letra legível sobre uma cor qualquer.
 *
 * Existe porque o usuário escolhe a cor e o sistema tem que garantir a
 * leitura sozinho — o CSS ainda não tem `contrast-color()` com suporte
 * amplo, então a decisão "letra clara ou escura" é tomada aqui e vai pro
 * `<html>` como variável pronta.
 */

export type Rgb = [number, number, number];

/** Aceita `#abc`, `abc`, `#aabbcc` ou `aabbcc`; devolve `#aabbcc` minúsculo, ou `null`. */
export function normalizeHex(input: string): string | null {
  const raw = input.trim().replace(/^#/, "").toLowerCase();
  if (/^[0-9a-f]{3}$/.test(raw)) return `#${raw.split("").map(c => c + c).join("")}`;
  if (/^[0-9a-f]{6}$/.test(raw)) return `#${raw}`;
  return null;
}

export function hexToRgb(hex: string): Rgb {
  const n = normalizeHex(hex);
  if (!n) throw new Error(`cor inválida: ${hex}`);
  return [parseInt(n.slice(1, 3), 16), parseInt(n.slice(3, 5), 16), parseInt(n.slice(5, 7), 16)];
}

export function rgbToHex([r, g, b]: Rgb): string {
  const part = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** Luminância relativa da WCAG 2.x. */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** `weight` de `a` (0..1) misturado com `b`, em sRGB — igual ao `color-mix` do CSS. */
export function mix(a: string, b: string, weight: number): string {
  const ra = hexToRgb(a);
  const rb = hexToRgb(b);
  return rgbToHex([0, 1, 2].map(i => ra[i] * weight + rb[i] * (1 - weight)) as Rgb);
}

export const LIGHT_TEXT = "#ffffff";
export const DARK_TEXT = "#16181c";

/** A letra (clara ou escura) que mais contrasta com `bg`. */
export function readableTextOn(bg: string): string {
  return contrastRatio(bg, LIGHT_TEXT) >= contrastRatio(bg, DARK_TEXT) ? LIGHT_TEXT : DARK_TEXT;
}

export function isLight(hex: string): boolean {
  return readableTextOn(hex) === DARK_TEXT;
}

/**
 * A cor mais próxima de `color` que passa de `min` sobre `bg`, escurecendo
 * (fundo claro) ou clareando (fundo escuro) aos poucos. Usada para o
 * destaque como LETRA: um amarelo escolhido pelo usuário vira um
 * mostarda legível sobre o branco, sem mudar o amarelo dos botões.
 */
export function ensureContrast(color: string, bg: string, min = 4.5): string {
  if (contrastRatio(color, bg) >= min) return color;
  const toward = isLight(bg) ? "#000000" : "#ffffff";
  for (let w = 0.95; w > 0; w -= 0.05) {
    const candidate = mix(color, toward, w);
    if (contrastRatio(candidate, bg) >= min) return candidate;
  }
  return toward;
}

/** Matiz (0–360), saturação e luminosidade (0–1). */
export function hexToHsl(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map(v => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, s, l];
}

export function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    hue < 60 ? [c, x, 0] :
    hue < 120 ? [x, c, 0] :
    hue < 180 ? [0, c, x] :
    hue < 240 ? [0, x, c] :
    hue < 300 ? [x, 0, c] : [c, 0, x];
  return rgbToHex([(r + m) * 255, (g + m) * 255, (b + m) * 255]);
}

/** Quão "colorida" é a cor (0 = cinza puro, 1 = cor pura): a distância entre
 *  o maior e o menor canal. */
export function chroma(hex: string): number {
  const rgb = hexToRgb(hex);
  return (Math.max(...rgb) - Math.min(...rgb)) / 255;
}

/** As duas cores das luzes de fundo (a orbe da Home e a da miniatura do
 *  tema), tiradas da paleta: as cores de cartão mais VIVAS, ignorando cinzas
 *  e grafites (temas claros usam cartões escuros neutros, e a luz ficava
 *  cinza). Sem duas cores vivas, completa com tons da própria cor de
 *  destaque — continua dentro da paleta. */
export function glowColors(accent: string, cards: readonly string[]): [string, string] {
  const accentHex = normalizeHex(accent) ?? "#305cff";
  const vivid = cards
    .map(card => normalizeHex(card))
    .filter((card): card is string => card !== null && chroma(card) >= 0.25 && card !== accentHex)
    .sort((a, b) => chroma(b) - chroma(a));
  const fill = [mix(accentHex, "#ffffff", 0.55), mix(accentHex, "#ffffff", 0.8)];
  const picked = [...vivid, ...fill];
  return [picked[0], picked[1]];
}
